import React, { ReactNode, MouseEvent, Children, useRef } from 'react';
import { Settings } from 'react-slick';
import cn from 'classnames';
import { UiSlider, UiTitle } from 'znanie-uikit';

import ImageProxy, { ImageProxyProps } from 'components/common/ImageProxy';
import { Link } from 'components/common/Link';

import { getFormattedText } from 'utils/helpers';

import { activateSlidesAndCopies, deactivateSlidesExcept } from './Cover.utils';

import css from './Cover.module.scss';

export * from './Cover.utils';

interface IProps {
  size?: 'small' | 'medium' | 'large' | 'intro';
  className?: string;
  wrapperClassName?: string;
  background?: string;
  vertical?: 'top' | 'center' | 'bottom';
  type?: 'image' | 'video' | 'broadcast' | 'slider';
  poster?: string;
  sliderSettings?: {
    dots?: boolean;
    dotsDecoration?: boolean;
    dotsReverse?: boolean;
    arrows?: boolean;
    autoplaySpeed?: number;
  };
  imgProxyProps?: ImageProxyProps;
  link?: string;
  children: ReactNode;
  isAuthorized?: boolean;

  onClick?(e: MouseEvent<HTMLAnchorElement>): void;
}

interface ContentProps {
  widget?: ReactNode;
  children: any;
}

interface ITopProps {
  widgets?: {
    left?: ReactNode;
    right?: ReactNode;
  };
  className?: string;
}

interface ICoverSliderItem {
  type?: 'image' | 'video';
  poster?: string;
  background: string;
  imgProxyProps?: ImageProxyProps;
  widgets?: any;
  children: ReactNode;
  className?: string;
  topWidgetsClassName?: string;
  onClick?: () => void;
}

const Cover = (props: IProps) => {
  const {
    size,
    className,
    wrapperClassName,
    background = '',
    poster,
    vertical = 'bottom',
    type = 'image',
    sliderSettings,
    imgProxyProps,
    link,
    children,
    onClick,
  } = props;

  const wrapperClasses = cn(
    css.wrapper,
    size && css[size],
    type === 'slider' && css.slider,
    vertical && css[vertical]
  );

  const classes = cn(css.container, className);

  const sliderElem = useRef<HTMLDivElement>(null);

  if (type === 'slider') {
    let getSettings = {
      slidesToShow: 1,
      slidesToScroll: 1,
      infinite: true,
      autoplay: true,
      autoplaySpeed: sliderSettings?.autoplaySpeed || 3000,
      fade: false,
      pauseOnDotsHover: true,
      pauseOnHover: false,
    } as Settings;

    if (sliderSettings?.dots) {
      getSettings = {
        ...{
          dots: true,
          dotsClass: cn(
            css.dotsClass,
            sliderSettings?.dotsReverse && css.reverse
          ),
          appendDots: UiSlider.Dots as any,
        },
        ...getSettings,
      };
    }

    if (sliderSettings?.arrows) {
      getSettings = {
        ...{
          arrows: true,
          nextArrow: <UiSlider.NextArrow />,
          prevArrow: <UiSlider.PrevArrow />,
        },
        ...getSettings,
      };
    }

    return (
      <div className={cn(css.sliderWrapper, wrapperClassName)}>
        <div className={wrapperClasses} ref={sliderElem}>
          <UiSlider
            className={cn(
              sliderSettings?.arrows && css.sliderWithArrows,
              sliderSettings?.dotsDecoration &&
                !sliderSettings.dotsReverse &&
                css.sliderWithDotsDecoration
            )}
            beforeChange={(prevIndex, nextIndex) => {
              // Делаем следующий слайд и его копии активными
              activateSlidesAndCopies(
                nextIndex,
                sliderElem,
                Children.count(children)
              );
            }}
            afterChange={(slideIndex) => {
              // Убираем активность всем слайдам, кроме текущего оригинала
              // (копии также деактивируются)
              deactivateSlidesExcept(slideIndex, sliderElem);
            }}
            {...getSettings}
          >
            {children}
          </UiSlider>
        </div>
      </div>
    );
  }

  if (link) {
    return (
      <div className={cn(wrapperClasses, wrapperClassName)}>
        <Link href={link}>
          <a className={classes} onClick={(e) => onClick && onClick(e)}>
            <div className={css.children}>{children}</div>
            <Background
              type={type}
              background={background}
              poster={poster}
              imgProxyProps={imgProxyProps}
            />
          </a>
        </Link>
      </div>
    );
  }

  return (
    <div className={cn(wrapperClasses, wrapperClassName)}>
      <div className={classes}>
        <div className={css.children}>{children}</div>
        <Background
          type={type}
          background={background}
          poster={poster}
          imgProxyProps={imgProxyProps}
        />
      </div>
    </div>
  );
};

const SliderItem = (props: ICoverSliderItem) => {
  const {
    type = 'image',
    background = '',
    imgProxyProps,
    poster,
    widgets,
    children,
    className,
    topWidgetsClassName,
    onClick,
  } = props;

  const classes = cn(css.container, className);

  return (
    <div className={classes} onClick={onClick}>
      <div className={css.children}>
        <Cover.Top
          widgets={{
            left: widgets?.top?.left,
            right: widgets?.top?.right,
          }}
          className={topWidgetsClassName}
        />
        <Cover.Content widget={widgets?.bottom?.right}>
          {children}
        </Cover.Content>
      </div>
      <Background
        type={type}
        background={background}
        poster={poster}
        isSlider
        imgProxyProps={imgProxyProps}
      />
    </div>
  );
};

const Background = ({
  type,
  background,
  imgProxyProps,
  poster,
  isSlider,
}: {
  type: 'image' | 'video' | 'broadcast';
  background: string;
  imgProxyProps?: ImageProxyProps;
  poster?: string;
  isSlider?: boolean;
}) => {
  return (
    <div
      className={cn(css.backgroundWrapper, {
        [css.backgroundBlackout]: !isSlider,
      })}
    >
      <div className={css.background}>
        {background && (
          <>
            {type === 'image' && (
              <ImageProxy
                src={background}
                layout="fill"
                objectFit="cover"
                quality="100"
                alt=""
                {...imgProxyProps}
              />
            )}
            {type === 'video' && (
              <video
                id="video"
                width="100%"
                height="auto"
                autoPlay
                playsInline
                loop
                muted
                preload="auto"
                poster={poster}
              >
                <source src={background} />
              </video>
            )}
            {type === 'broadcast' && (
              <iframe
                className={css.broadcast}
                src={background}
                title={''}
                frameBorder="0"
                allowFullScreen
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Top = ({ widgets, className }: ITopProps) => {
  return (
    <ul className={cn(css.topWidgets, className)}>
      <li>{widgets?.left}</li>
      <li>{widgets?.right}</li>
    </ul>
  );
};

const Content = ({ widget, children }: ContentProps) => {
  const classes = cn(css.content, !widget && css.withoutWidget);

  return (
    <div className={classes}>
      {children}
      {widget && <div className={css.right}>{widget}</div>}
    </div>
  );
};

const Buttons = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => {
  const classes = cn(css.buttons, className);

  return (
    <div className={classes}>
      <div className={css.buttonsInner}>{children}</div>
    </div>
  );
};

const Title = ({
  children,
  withFormat,
  className,
  as = 'div',
}: {
  children: string | ReactNode;
  withFormat?: boolean;
  className?: string;
  as?: string;
}) => {
  let content = children;

  if (typeof content === 'string' && withFormat) {
    return (
      <UiTitle as={as} className={cn(css.title, className)} color="white">
        <span
          dangerouslySetInnerHTML={{
            __html: getFormattedText(content, '<br/>'),
          }}
        />
      </UiTitle>
    );
  }

  return <div className={cn(css.title, className)}>{content}</div>;
};

const Description = ({
  children,
  withFormat,
}: {
  children: string;
  withFormat?: boolean;
}) => {
  const result = children;

  if (withFormat) {
    return (
      <div
        className={css.description}
        dangerouslySetInnerHTML={{ __html: getFormattedText(result, '<br/>') }}
      />
    );
  }
  return <div className={css.description}>{result}</div>;
};

const LinkTo = ({
  href,
  children,
  onClick,
}: {
  href: string;
  children: ReactNode;
  onClick?(): void;
}) => {
  if (!children || !href) return null;

  return (
    <Link href={href}>
      <a>
        <div onClick={onClick} className={css.linkTo}>
          {children}
        </div>
      </a>
    </Link>
  );
};

Cover.Top = Top;
Cover.Content = Content;
Cover.Buttons = Buttons;
Cover.Title = Title;
Cover.Description = Description;
Cover.SliderItem = SliderItem;
Cover.LinkTo = LinkTo;

export default Cover;
